import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

const API = '/api';

/**
 * EnergyControlEmailReport — request a full Energy Control report by email.
 * Fallback-friendly: report stays visible on the page even if the email fails.
 */
export default function EnergyControlEmailReport({ intakeId, defaultEmail }) {
  const [email, setEmail] = useState(defaultEmail || '');
  const [loading, setLoading] = useState(false);
  // status: idle | success | error
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());

  const handleSend = async () => {
    if (!valid) {
      toast.error('Vul een geldig e-mailadres in');
      return;
    }
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.post(`${API}/energy-control/email-report`, {
        intake_id: intakeId,
        email: email.trim(),
      });
      if (window.pwTrack) window.pwTrack('energy_control_report_email_request', {
        intake_id: intakeId,
        success: !!res.data?.success,
      });
      if (res.data?.success) {
        setStatus('success');
        toast.success('E-mail verzonden');
      } else {
        setStatus('error');
        setErrorMsg(res.data?.message || 'Onbekende fout.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.message || err.message;
      setStatus('error');
      setErrorMsg(typeof detail === 'string' ? detail : 'Verzenden mislukt.');
      if (window.pwTrack) window.pwTrack('energy_control_report_email_request', {
        intake_id: intakeId,
        success: false,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-6 pb-2">
      <div className="max-w-2xl mx-auto" data-testid="ec-email-report">
        <div className="bg-white border border-border rounded-2xl p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-medium text-primary leading-tight" style={{ fontFamily: 'Outfit' }}>
                Stuur mijn volledige Energy Control Plan per e-mail
              </h3>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                We sturen u een overzichtelijk rapport met alle 8 blokken, uw grootste kans en logische vervolgstappen.
              </p>
            </div>
          </div>

          {status !== 'success' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
                <div>
                  <Label htmlFor="ec-email-report-input" className="sr-only">E-mailadres</Label>
                  <Input
                    id="ec-email-report-input"
                    type="email"
                    autoComplete="email"
                    placeholder="uw@email.nl"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
                    disabled={loading}
                    data-testid="ec-email-report-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!valid || loading}
                  className="btn-primary inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-50"
                  data-testid="ec-email-report-send-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  {loading ? 'Verzenden…' : 'Verstuur per e-mail'}
                </button>
              </div>

              {status === 'error' && (
                <div
                  className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5 text-sm text-amber-900"
                  data-testid="ec-email-report-error"
                >
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium">E-mail kon niet worden verzonden.</div>
                    <div className="text-xs mt-0.5">Uw rapport blijft hier beschikbaar. {errorMsg ? <span className="opacity-75">({errorMsg})</span> : null}</div>
                  </div>
                </div>
              )}
            </>
          )}

          {status === 'success' && (
            <div
              className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2.5 text-sm text-emerald-900"
              data-testid="ec-email-report-success"
            >
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium">Uw Energy Control Plan is verzonden.</div>
                <div className="text-xs mt-0.5">Verzonden naar <span className="font-mono">{email}</span> · controleer ook uw spammap.</div>
              </div>
            </div>
          )}

          <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
            Geen verplichtingen · uw rapport blijft online beschikbaar als terugvaloptie.
          </p>
        </div>
      </div>
    </div>
  );
}
